import { Head, Link, router, useForm } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

import CaseForm from "./_form"
import type { EmployeeAccessData } from "./_form"

interface Option {
  id: number
  name: string
}

interface StatusOption {
  value: number
  label: string
}

interface CaseRecord {
  id: number
  case_number: string | null
  display_name: string | null
  file_number: string
  case_reference: string
  hpi_name: string
  qb_name: string
  report_name: string
  directory: string
  customer_id: number | null
  customer_user_id: number | null
  customer_location_id: number | null
  investigation_type_id: number | null
  manager_id: number | null
  referral_case_id: number | null
  location_city: string
  location_state: string
  open_date: string | null
  closed_date: string | null
  accepted_date: string | null
  paid_date: string | null
  budget: number | null
  retainer: number | null
  results: number | null
  results_notes: string
  notes: string
  objectives: string
  internal_notes: string
  has_attorney: boolean
  attorney_name: string
  attorney_phone: string
  attorney_law_firm: string
  attorney_email: string
  prepared_for: string
  matter: string
  accepted: number | null
  accepted_text: string
  after_hours: boolean
  is_public: boolean
  hpi_company: number | null
  status: number
  intake_by_id: number | null
  other_type: string
  insurance_days: number | null
  insurance_budget_notes: string
  referral_customer_id: number | null
  referral_customer_user_id: number | null
}

interface EmployeeCheckbox {
  id: number
  name: string
  checked: boolean
}

interface NotifyEmployee {
  id: number
  name: string
  forced: boolean
  default_checked: boolean
}

interface Props {
  case_record: CaseRecord
  customer_display_name?: string
  investigation_types: Option[]
  managers: Option[]
  users: Option[]
  status_options: StatusOption[]
  allow_employees?: EmployeeCheckbox[]
  deny_employees?: EmployeeCheckbox[]
  notify_employees?: NotifyEmployee[]
  referral_display_name?: string
  can_close_case?: boolean
  errors?: Record<string, string[]>
}

export default function Edit({
  case_record,
  customer_display_name = "",
  investigation_types,
  managers,
  users,
  status_options,
  allow_employees,
  deny_employees,
  notify_employees,
  referral_display_name = "",
  can_close_case = false,
  errors,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name ?? case_record.case_number ?? "Case",
      href: `/cases/${case_record.id}`,
    },
    { title: "Edit", href: `/cases/${case_record.id}/edit` },
  ]

  const { data, setData, processing } = useForm<CaseRecord>(case_record)

  const handleSubmit = (
    e: React.FormEvent,
    employeeData: EmployeeAccessData,
  ) => {
    e.preventDefault()
    router.patch(`/cases/${case_record.id}`, { ...data, ...employeeData })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Edit ${case_record.case_number ?? "Case"}`} />

      <div className="p-6">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">
            Edit Case: {case_record.case_number ?? case_record.hpi_name}
          </h1>
          {can_close_case && (
            <Link
              href={`/cases/${case_record.id}/close`}
              className="bg-hpi-red hover:bg-hpi-red/80 rounded px-4 py-2 text-sm font-medium text-white"
            >
              Close Case
            </Link>
          )}
        </div>

        <CaseForm
          data={data}
          setData={setData}
          errors={errors}
          processing={processing}
          onSubmit={handleSubmit}
          submitLabel="Update Case"
          customer_display_name={customer_display_name}
          investigation_types={investigation_types}
          managers={managers}
          users={users}
          status_options={status_options}
          referral_display_name={referral_display_name}
          isEdit={true}
          allow_employees={allow_employees}
          deny_employees={deny_employees}
          notify_employees={notify_employees}
        />
      </div>
    </AppLayout>
  )
}
